import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import apiClient from '@/utils/api';
import { setAuthToken, setUser, initiateGoogleAuth } from '@/utils/auth';
import { toast } from 'sonner';
import TurnstileWidget from '@/components/common/TurnstileWidget';

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({ name: '', email: '', password: '', confirm_password: '', phone: '' });
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);

  // Get redirect URL from query params
  const redirectUrl = searchParams.get('redirect') || '/dashboard';
  const action = searchParams.get('action');

  const passwordsMatch = formData.password === formData.confirm_password;
  const canSubmit = formData.name && formData.email && formData.password && formData.confirm_password && passwordsMatch && captchaToken && !loading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!passwordsMatch) {
      toast.error('Passwords do not match');
      return;
    }
    if (!captchaToken) {
      toast.error('Please complete the CAPTCHA verification');
      return;
    }
    setLoading(true);

    try {
      const response = await apiClient.post('/auth/register', { ...formData, captcha_token: captchaToken });
      setAuthToken(response.data.token);
      setUser(response.data.user);
      toast.success('Account created successfully!');

      // ---- Referral capture (Phase 3) ----
      const refCode = searchParams.get('ref') || localStorage.getItem('zenstudios_invite_code');
      const newUserId = response.data.user?.user_id || response.data.user?.id;
      if (refCode && newUserId) {
        try {
          await apiClient.post('/referrals/register', {
            code: refCode,
            referred_user_id: newUserId,
            referred_user_type: 'consumer',
          });
        } catch { /* non-fatal */ }
      }
      
      // Handle different action types
      if (action === 'save_quote' || action === 'book_consultation') {
        // Redirect to interior design page where the pending quote will be restored
        navigate('/interior-design');
      } else {
        navigate(redirectUrl);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F5F5F0] py-32 px-6" data-testid="register-page">
      <div className="w-full max-w-md bg-white p-12 shadow-lg">
        <h1 className="text-4xl mb-2 text-center" data-testid="register-title">Create Account</h1>
        <p className="text-center text-gray-600 mb-8">Join ZenStudios today</p>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label htmlFor="name">Full Name</Label>
            <Input
              id="name"
              type="text"
              className="rounded-none border-b border-t-0 border-l-0 border-r-0 px-0"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              data-testid="register-name-input"
            />
          </div>
          
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              className="rounded-none border-b border-t-0 border-l-0 border-r-0 px-0"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              data-testid="register-email-input"
            />
          </div>
          
          <div>
            <Label htmlFor="phone">Phone (Optional)</Label>
            <Input
              id="phone"
              type="tel"
              className="rounded-none border-b border-t-0 border-l-0 border-r-0 px-0"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              data-testid="register-phone-input"
            />
          </div>
          
          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              className="rounded-none border-b border-t-0 border-l-0 border-r-0 px-0"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              minLength={6}
              data-testid="register-password-input"
            />
          </div>
          
          <div>
            <Label htmlFor="confirm_password">Confirm Password</Label>
            <Input
              id="confirm_password"
              type="password"
              className={`rounded-none border-b border-t-0 border-l-0 border-r-0 px-0 ${formData.confirm_password && !passwordsMatch ? 'border-red-500' : ''}`}
              value={formData.confirm_password}
              onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
              required
              data-testid="register-confirm-password-input"
            />
            {formData.confirm_password && !passwordsMatch && (
              <p className="text-red-500 text-xs mt-1" data-testid="password-mismatch-error">Passwords do not match</p>
            )}
          </div>

          <TurnstileWidget
            onVerify={setCaptchaToken}
            onExpire={() => setCaptchaToken(null)}
            className="flex justify-center"
          />
          
          <Button 
            type="submit" 
            className="w-full rounded-none bg-primary text-primary-foreground py-6"
            disabled={!canSubmit}
            data-testid="register-submit-btn"
          >
            {loading ? 'Creating account...' : 'Create Account'}
          </Button>
        </form>
        
        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Or continue with</span>
            </div>
          </div>
          
          <Button
            type="button"
            variant="outline"
            className="w-full mt-6 rounded-none py-6"
            onClick={initiateGoogleAuth}
            data-testid="register-google-btn"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign up with Google
          </Button>
        </div>
        
        <p className="mt-6 text-center text-sm text-gray-600">
          Already have an account?{' '}
          <a href="/login" className="text-primary hover:underline" data-testid="register-login-link">
            Sign in
          </a>
        </p>
      </div>
    </div>
  );
};

export default Register;